import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2 } from 'lucide-react';
import { NumericInput } from "@/components/ui/numeric-input";

import { toVnDateString } from "@apv-erp/status";
const formSchema = z.object({
    contractNumber: z.string().min(1, 'Vui lòng nhập số hợp đồng'),
    contractType: z.string().min(1, 'Vui lòng chọn loại hợp đồng'),
    startDate: z.string().min(1, 'Vui lòng chọn ngày bắt đầu'),
    endDate: z.string().optional().nullable(),
    basicSalary: z.number().optional().nullable(),
    status: z.string().min(1, 'Vui lòng chọn trạng thái'),
});

type FormValues = z.infer<typeof formSchema>;

interface Props {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    userId: string;
    initialData?: any;
    onSubmit: (data: any) => Promise<void>;
    isSubmitting: boolean;
}

export function EmployeeContractForm({
    open,
    onOpenChange,
    userId,
    initialData,
    onSubmit,
    isSubmitting
}: Props) {
    const form = useForm<FormValues>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            contractNumber: '',
            contractType: 'DEFINITE',
            startDate: toVnDateString(),
            endDate: '',
            basicSalary: 0,
            status: 'DRAFT',
        }
    });

    useEffect(() => {
        if (initialData && open) {
            form.reset({
                contractNumber: initialData.contractNumber,
                contractType: initialData.contractType || initialData.type,
                startDate: initialData.startDate ? toVnDateString(initialData.startDate) : '',
                endDate: initialData.endDate ? toVnDateString(initialData.endDate) : '',
                basicSalary: initialData.basicSalary || 0,
                status: initialData.status,
            });
        } else if (open) {
            form.reset({
                contractNumber: '',
                contractType: 'DEFINITE',
                startDate: toVnDateString(),
                endDate: '',
                basicSalary: 0,
                status: 'DRAFT',
            });
        }
    }, [initialData, open, form]);

    const handleSubmit = async (values: FormValues) => {
        const payload = {
            ...values,
            userId,
            endDate: values.endDate || null,
            basicSalary: values.basicSalary ? Number(values.basicSalary) : null,
        };
        await onSubmit(payload);
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                    <DialogTitle>
                        {initialData ? 'Sửa hợp đồng' : 'Thêm hợp đồng mới'}
                    </DialogTitle>
                </DialogHeader>

                <Form {...form}>
                    <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <FormField
                                control={form.control}
                                name="contractNumber"
                                render={({ field }) => (
                                    <FormItem className="col-span-2">
                                        <FormLabel>Số hợp đồng</FormLabel>
                                        <FormControl>
                                            <Input placeholder="VD: HDLD-2024-001" {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="contractType"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Loại hợp đồng</FormLabel>
                                        <Select onValueChange={field.onChange} defaultValue={field.value} value={field.value}>
                                            <FormControl>
                                                <SelectTrigger>
                                                    <SelectValue placeholder="Chọn loại HĐ" />
                                                </SelectTrigger>
                                            </FormControl>
                                            <SelectContent>
                                                <SelectItem value="PROBATION">Thử việc</SelectItem>
                                                <SelectItem value="DEFINITE">Xác định thời hạn</SelectItem>
                                                <SelectItem value="INDEFINITE">Không xác định thời hạn</SelectItem>
                                                <SelectItem value="SEASONAL">Thời vụ</SelectItem>
                                            </SelectContent>
                                        </Select>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="status"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Trạng thái</FormLabel>
                                        <Select onValueChange={field.onChange} defaultValue={field.value} value={field.value}>
                                            <FormControl>
                                                <SelectTrigger>
                                                    <SelectValue placeholder="Chọn trạng thái" />
                                                </SelectTrigger>
                                            </FormControl>
                                            <SelectContent>
                                                <SelectItem value="DRAFT">Nháp</SelectItem>
                                                <SelectItem value="ACTIVE">Đang hiệu lực</SelectItem>
                                                <SelectItem value="EXPIRED">Đã hết hạn</SelectItem>
                                                <SelectItem value="TERMINATED">Đã chấm dứt</SelectItem>
                                            </SelectContent>
                                        </Select>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="startDate"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Ngày bắt đầu</FormLabel>
                                        <FormControl>
                                            <Input type="date" {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="endDate"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Ngày kết thúc</FormLabel>
                                        <FormControl>
                                            <Input type="date" {...field} value={field.value || ''} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="basicSalary"
                                render={({ field }) => (
                                    <FormItem className="col-span-2">
                                        <FormLabel>Lương cơ bản</FormLabel>
                                        <FormControl>
                                            <NumericInput {...field} value={field.value || ''} onChange={e => field.onChange(e.target.value ? Number(e.target.value) : null)} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>

                        <div className="flex justify-end gap-3 pt-4 border-t mt-4">
                            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
                                Huỷ
                            </Button>
                            <Button type="submit" disabled={isSubmitting} className="bg-indigo-600 hover:bg-indigo-700">
                                {isSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                                Lưu Hợp Đồng
                            </Button>
                        </div>
                    </form>
                </Form>
            </DialogContent>
        </Dialog>
    );
}
